<template>
  <div class="bg-white divide-y-2 rounded-lg shadow-default overflow-hidden">
    <div>
      <div class="relative flex flex-wrap items-start p-5">
        <div class="flex h-11 w-11 flex-none items-start justify-center">
          <NuxtImg
            loading="lazy"
            v-if="selectedService?.icon"
            :src="selectedService?.icon?.permalink"
            class="w-9"
            alt="clinic icon"
            width="36"
            height="36"
          />
        </div>

        <div v-if="everywhereNurseServiceIsFormActive" class="flex flex-col flex-1 mt-1 mr-2 md:mx-3">
          <div class="font-bold">
            {{
              everywhereNurseServiceLocationAvailabilityStatus == "idle"
                ? selectedService?.title
                : `${$t("getStarted.everywhereNurseService.searchErrorTitle", {
                    searchTerm: everywhereNurseServiceSearchTerm,
                  })}`
            }}
          </div>
          <div class="my-2 text-sm leading-6 md:mr-8 text-left">
            {{
              everywhereNurseServiceLocationAvailabilityStatus == "idle"
                ? selectedService?.short_description
                : $t("getStarted.everywhereNurseService.searchErrorDescription")
            }}
          </div>
        </div>
        <div v-else class="flex-1">
          <div class="font-bold">
            {{
              $t("getStarted.everywhereNurseService.searchSuccessTitle", {
                searchTerm: everywhereNurseServiceSearchTerm,
              })
            }}
          </div>
          <BaseButton
            :text="$t('getStarted.everywhereNurseService.buttons.chooseTest')"
            class="ml-auto mt-12"
            @click="handleNavigateToEverywhereNurseServiceTests"
          />
        </div>

        <div v-if="everywhereNurseServiceIsFormActive" class="flex flex-col flex-1 basis-full flex-wrap gap-2 mt-2">
          <div v-if="!isEveryWherePriceLookup">
            <ClinicMapSearchBox
              @onSearchClick="onEverywhereSearchClicked"
              showSearchBox
              button-type="submit"
              :reset-scroll="false"
              name="town_or_postcode"
              class="basis-full mx-0 -mb-4"
              :loading="everywhereNurseServiceMutationStatus === 'pending'"
              disable-default-css
              :isEverywhereNurseService="true"
            />
          </div>
          <ProductNurseServiceForm
            class="mt-2 mx-14"
            v-else
            isGetStarted
            @onSubmit="onEverywhereSearchClicked"
          ></ProductNurseServiceForm>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useGlobalStore } from "@/stores/global";
import { useGoogleMapsLoader } from "@/composables/useGoogleMapsLoader";
const route = useRoute();
const mapStore = useMapStore();
const globalStore = useGlobalStore();

const getStartedModalStore = useGetStartedModalStore();
const checkoutStore = useCheckoutStore();

const selectedService = computed(() => getStartedModalStore.selectedService);
mapStore.setPanelTestsLoading(true);
try {
  const { data: products } = await useFetchProductList({
    "service_type[0]": "everywhere-nurse-service",
  });
  mapStore.getTests(products.value?.data ?? []);
} finally {
  mapStore.setPanelTestsLoading(false);
}

// Set clinic id to store.
checkoutStore.setSelectedClinicId("everywhere");

const everywhereNurseServiceSearchTerm = ref<string>(String(route.query.searchTerm));
const everywhereNurseServiceLocationAvailabilityStatus = ref("idle");
const everywhereNurseServiceMutationStatus = ref("idle");
const everywhereNurseServiceIsFormActive = computed(() =>
  ["idle", "unavailable"].includes(everywhereNurseServiceLocationAvailabilityStatus.value)
);

const isEveryWherePriceLookup = globalStore.siteEverywhereRegionLookUp;

async function onEverywhereSearchClicked(searchData) {
  globalStore.setClinicMapSearchText(searchData);
  if (!searchData) {
    everywhereNurseServiceLocationAvailabilityStatus.value = "idle";
    return;
  }
  const result = await findLatLang(searchData);
  if (result) {
    everywhereNurseServiceSearchTerm.value = String(searchData);
    everywhereNurseServiceLocationAvailabilityStatus.value = result ? "available" : "unavailable";
    everywhereNurseServiceMutationStatus.value = "idle";
  }
}

async function findLatLang(address) {
  const loader = useGoogleMapsLoader();
  const apiPromise = await loader.load();
  const geoCoder = new apiPromise.maps.Geocoder();
  try {
    const results = await geoCoder.geocode({ address: address });
    const { data: resultChecked } = await useCheckEverywhereService(
      results.results[0].geometry.location.lat(),
      results.results[0].geometry.location.lng()
    );
    return resultChecked.value.available;
  } catch (error) {
    everywhereNurseServiceLocationAvailabilityStatus.value = "unavailable";
    everywhereNurseServiceSearchTerm.value = String(address);
    everywhereNurseServiceMutationStatus.value = "idle";

    return false;
  }
}

async function handleNavigateToEverywhereNurseServiceTests() {
  globalStore.setClinicMapSearchText(everywhereNurseServiceSearchTerm.value);
  await navigateTo({
    query: {
      ...route.query,
      searchTerm: everywhereNurseServiceSearchTerm.value,
    },
  });

  openModal();
}

function openModal() {
  getStartedModalStore.navigate("show_tests");
  getStartedModalStore.open({
    clinic_view: "map",
    title: "",
    slug: "",
    ...selectedService.value,
    content: everywhereNurseServiceSearchTerm.value,
  });
}
onMounted(() => {
  if (!route.query.searchTerm) return;
  openModal();
});
</script>
